import { useState } from 'react';
import { motion } from 'framer-motion';
import DashboardHome from './DashboardHome';
import { TemplatesManagement } from './TemplatesManagement';
import TemplateManager from './components/TemplateManager';
import TemplateSyncStatus from './components/TemplateSyncStatus';
import { useTemplateSync } from './hooks/useTemplateSync';

function App() {
  const { isSyncing, syncResult, error } = useTemplateSync();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState('dashboard');

  const navigation = [
    { name: 'Dashboard', href: '#', icon: '📊', current: currentPage === 'dashboard' },
    { name: 'Templates', href: '#', icon: '📄', current: currentPage === 'templates' },
    { name: 'Template Manager', href: '#', icon: '🔧', current: currentPage === 'template-manager' },
    { name: 'Génération', href: '#', icon: '⚡', current: currentPage === 'generation' },
    { name: 'Statistiques', href: '#', icon: '📈', current: currentPage === 'stats' },
    { name: 'Paramètres', href: '#', icon: '⚙️', current: currentPage === 'settings' },
  ];

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'templates':
        return <TemplatesManagement onNavigateToTemplateManager={() => setCurrentPage('template-manager')} />;
      case 'template-manager':
        return <TemplateManager />;
      case 'dashboard':
      default:
        return <DashboardHome />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 lg:flex">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out flex flex-col ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:static lg:translate-x-0 lg:transform-none lg:h-screen lg:w-64`}>
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900">Busol CV Service</h1>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <nav className="mt-5 px-2">
          <div className="space-y-1">
            {navigation.map((item) => (
              <button
                key={item.name}
                onClick={() => {
                  // Mapping correct des noms de pages
                  const pageMap: { [key: string]: string } = {
                    'Dashboard': 'dashboard',
                    'Templates': 'templates',
                    'Template Manager': 'template-manager',
                    'Génération': 'generation',
                    'Statistiques': 'stats',
                    'Paramètres': 'settings'
                  };
                  setCurrentPage(pageMap[item.name] || 'dashboard');
                  setSidebarOpen(false);
                }}
                className={`${
                  item.current
                    ? 'bg-blue-50 border-blue-500 text-blue-700'
                    : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                } group flex items-center px-2 py-2 text-sm font-medium rounded-md border-l-4 transition-colors duration-200`}
              >
                <span className="mr-3 text-lg">{item.icon}</span>
                {item.name}
              </button>
            ))}
          </div>
        </nav>

        {/* Sync Status in Sidebar */}
        <div className="mt-auto p-4 border-t border-gray-200 max-h-48 overflow-y-auto">
          <TemplateSyncStatus />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 min-h-screen">
        {/* Top Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <h2 className="ml-2 text-2xl font-bold text-gray-900">
                  {navigation.find(nav => nav.current)?.name || 'Dashboard'}
                </h2>
              </div>
              
              <div className="flex items-center space-x-4">
                {isSyncing && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center text-sm text-blue-600"
                  >
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    Syncing...
                  </motion.div>
                )}
                {syncResult && (
                  <div className="text-sm text-green-600">
                    ✓ {syncResult.synced_count} templates
                  </div>
                )}
                {error && (
                  <div className="text-sm text-red-600">
                    ✗ Sync failed
                  </div>
                )}
                
                <div className="flex items-center space-x-2">
                  <button className="p-2 text-gray-400 hover:text-gray-600">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5-5-5h5v-5a7.5 7.5 0 1 0-15 0v5h5l-5 5-5-5h5v-5a10 10 0 1 1 20 0v5z" />
                    </svg>
                  </button>
                  <button className="p-2 text-gray-400 hover:text-gray-600">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5-5-5h5v-5a7.5 7.5 0 1 0-15 0v5h5l-5 5-5-5h5v-5a10 10 0 1 1 20 0v5z" />
                    </svg>
                  </button>
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-medium">A</span>
                    </div>
                    <span className="text-sm text-gray-700">Admin User</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="px-4 sm:px-6 lg:px-8 py-8">
          {renderCurrentPage()}
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}

export default App;

